import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/24/outline';

import { useWebEDIStore } from 'stores/webedi/webedi';
import { isPlaceholderTransportUnit } from 'support/helpers/packingList/defaultTransportUnit';
import { TransactionState } from 'support/types';
import { WebEDIRowData } from 'support/types/webediTable';
import { Button } from 'components/Form/Button/Button';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';

import { useSsccLabels } from '../hooks/useSsccLabels';

export const SsccLabelBulkActions = () => {
  const { t } = useTranslation();
  const { print, download, isPrinting, isDownloading } = useSsccLabels();
  const isMessageInProgress = useWebEDIStore((state) => state.messageState === TransactionState.pending);
  const transportUnits = (useWatch({ name: 'transport_units' }) ?? []) as Array<WebEDIRowData>;
  const realUnits = transportUnits.filter((unit) => !isPlaceholderTransportUnit(unit));

  if (!realUnits.length) {
    return null;
  }

  const pdfUrls = realUnits
    .map((unit) => (unit.transport_label as { pdf_url?: string } | undefined)?.pdf_url)
    .filter((url): url is string => Boolean(url));

  if (pdfUrls.length !== realUnits.length) {
    return isMessageInProgress ? <LoadingSpinner sizeClassName="size-5" /> : null;
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        size="small"
        LeftIcon={PrinterIcon}
        loading={isPrinting}
        disabled={isPrinting}
        onClick={() => print(pdfUrls)}
        analyticsId="webedi:transport_units_print_all_sscc_labels"
      >
        {t('webedi:transport_units.sscc_label.print_all_button.text')}
      </Button>
      <Button
        variant="secondary"
        size="small"
        LeftIcon={ArrowDownTrayIcon}
        loading={isDownloading}
        disabled={isDownloading}
        onClick={() => download(pdfUrls)}
        analyticsId="webedi:transport_units_download_all_sscc_labels"
      >
        {t('webedi:transport_units.sscc_label.download_all_button.text')}
      </Button>
    </div>
  );
};
